import { Builder } from "@builder.io/react";
import { IBadgeList } from "@faire/web-api--source/indigofair/badges/IBadgeList";
import { IApplicableMembershipBenefits } from "@faire/web-api--source/indigofair/data/IApplicableMembershipBenefits";
import { IBrandTile } from "@faire/web-api--source/indigofair/data/IBrandTile";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { IItemPromoPrice } from "@faire/web-api--source/indigofair/data/IItemPromoPrice";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { IQuickAdd } from "@faire/web-api--source/indigofair/data/IQuickAdd";
import { isProduction } from "@faire/web/common/user-agent/isProduction";

import { IBrandInput } from "./BuilderRetailerBrandTileSection";
import { IProductInput } from "./BuilderRetailerProductTileSection";

export function deduplicateProductsOrBrands<
  T extends IProductInput | IBrandInput,
>(items: T[], deduplicateByStagingToken: boolean): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const token = deduplicateByStagingToken
      ? item.tokenStaging
      : item.tokenProd;

    if (seen.has(token)) {
      return false;
    }
    seen.add(token);
    return true;
  });
}

export const shouldRenderProductionTile = () => {
  return !Builder.isEditing && isProduction();
};

export const MOCK_PRODUCT_TILE = ILeanProductTile.build({
  product: ILeanProductTile.ILeanProduct.build({
    token: "p_v3rscehvrt",
    name: "MOCK PRODUCT - Remember to add a real product!",
    brand_token: "b_6afyx18cny",
    is_new: false,
    maker_best_seller: false,
    per_style_min_order_quantity: 0,
    state: "FOR_SALE",
    preorderable: false,
    is_promoted: false,
    impression_id: "1757518067_yrzpctuxzszz8za",
    avg_brand_review_rating: "4.7",
    brand_review_count: 238,
    board_tokens: [],
    promoted_by: [],
  }),
  badge_list: IBadgeList.build({
    badges: [],
    badge_xp_settings: [],
  }),
  best_image: IImage.build({
    token: "i_btcgsuxn95",
    width: 1250,
    height: 1250,
    sequence: 0,
    url: "https://cdn.faire.com/fastly/8d6e8b1afb516a24cb6be10610d1cea8fdd5c20f31474ddb3d71579212dbbb57.jpeg",
    type: "HERO",
    tags: ["Hero", "cloudinary_crop_fill"],
    crop_parameters: [],
  }),
  quick_add: IQuickAdd.build({
    quick_add_text: "+ Quick Add Case",
    quick_add_option: IQuickAdd.IQuickAddOption.build({
      option_token: "po_2szc6yrhcy",
      option_unit_multiplier: 1,
      option_min_order_quantity: 1,
      option_available_units: 17,
    }),
    mobile_quick_add_text: "Case of 1",
  }),
  min_option_wholesale_price: IFaireMoney.build({
    amount_cents: 4049,
    currency: "CAD",
    original_amount_cents: 2494,
    original_currency: "EUR",
  }),
  min_option_wholesale_promo_price: IItemPromoPrice.build({
    brand_promo_token: "bp_95cmwe6muw",
    discount_bps: 4000,
    discount_amount: IFaireMoney.build({
      amount_cents: 998,
      currency: "EUR",
    }),
    original_item_price: IFaireMoney.build({
      amount_cents: 2494,
      currency: "EUR",
    }),
    discounted_item_price: IFaireMoney.build({
      amount_cents: 2428,
      currency: "CAD",
      original_amount_cents: 1496,
      original_currency: "EUR",
    }),
  }),
  min_option_retail_price: IFaireMoney.build({
    amount_cents: 8108,
    currency: "CAD",
    original_amount_cents: 4995,
    original_currency: "EUR",
  }),
  has_active_brand_promo: false,
  is_highlighted_product_for_promotional_event: false,
  case_size_text: "Case of 1",
  min_option_brand_code: "JABR092AG-M",
  based_in_country: "BEL",
  show_location_badging: "DO_NOT_SHOW",
  images: [
    IImage.build({
      token: "i_btcgsuxn95",
      width: 1250,
      height: 1250,
      sequence: 0,
      url: "https://cdn.faire.com/fastly/8d6e8b1afb516a24cb6be10610d1cea8fdd5c20f31474ddb3d71579212dbbb57.jpeg",
      type: "HERO",
      tags: ["Hero", "cloudinary_crop_fill"],
      crop_parameters: [],
    }),
    IImage.build({
      token: "i_wwhavmvyjb",
      width: 1875,
      height: 1250,
      sequence: 1,
      url: "https://cdn.faire.com/fastly/275e59969d05b240bebc0d5227085423d0141656dd5000a451d8b665c5d4880a.jpeg",
      tags: ["cloudinary_crop_fill"],
      crop_parameters: [],
    }),
    IImage.build({
      token: "i_5ewtpm7xux",
      width: 1875,
      height: 1250,
      sequence: 2,
      url: "https://cdn.faire.com/fastly/282b1844bb52fb8ed23a6e356f7fd21a2f0c1c6d4231d21082686e56ce4ee178.jpeg",
      tags: ["cloudinary_crop_fill"],
      crop_parameters: [],
    }),
    IImage.build({
      token: "i_aqtd96zh93",
      width: 1250,
      height: 1250,
      sequence: 3,
      url: "https://cdn.faire.com/fastly/1b0c685fc511b1f99f30c54750ac9a27061454614ed6e65fe64448d346ddc425.jpeg",
      tags: ["cloudinary_crop_fill"],
      crop_parameters: [],
    }),
    IImage.build({
      token: "i_fr4x2r5es5",
      width: 1250,
      height: 1250,
      sequence: 4,
      url: "https://cdn.faire.com/fastly/45450c2808e193789239443ad8bf3d71610fdde51127d4b0bd4ac2be55caaca6.jpeg",
      tags: ["cloudinary_crop_fill"],
      crop_parameters: [],
    }),
  ],
  settings_to_assign_on_view: ["PRODUCT_TILE_FIRST_ITERATION_250506"],
});

export const MOCK_BRAND: Record<string, ILeanProductTile.IBrand> = {
  b_6afyx18cny: ILeanProductTile.IBrand.build({
    token: "b_6afyx18cny",
    name: "MOCK BRAND",
    insider_benefits: IApplicableMembershipBenefits.build({
      discount_percentage: 0,
      benefits: [],
    }),
    forbids_online_only_retailers: false,
    minimum_order_amount: IFaireMoney.build({
      amount_cents: 15000,
      currency: "CAD",
    }),
    ships_from_country: "BEL",
    based_in: "BEL",
    is_highlighted_brand_for_promotional_event: false,
    is_top_shop: true,
    active_products_count: 1100,
    based_in_country_message: {
      desktop_message: "Belgium",
      mobile_message: "Belgium",
    },
    profile_image: IImage.build({
      token: "i_hdy9yf3e6f",
      width: 300,
      height: 300,
      sequence: 0,
      url: "https://cdn.faire.com/fastly/0170a69b2a43c8e093368633b6b4ca71894e20182d2db3556029ad274430a7ab.jpeg",
      tags: [],
      crop_parameters: [],
    }),
    avg_brand_review_rating: "4.7",
  }),
};

export const MOCK_BRAND_TILE = IBrandTile.build({
  token: "b_6afyx18cny",
  name: "MOCK BRAND - Remember to add a real brand!",
  image: IImage.build({
    token: "i_vpnyhqycwe",
    width: 3500,
    height: 5250,
    url: "https://cdn.faire.com/fastly/b320a7dafa2ad725224ff5e9d17b0b9340a6a6de7a713da5d1689b1d7232cd6e.jpeg",
    tags: ['manual_image_crop:{"x":0,"y":814,"width":3500,"height":3500}'],
    crop_parameters: [],
  }),
  minimum_order_amount_cents: 15000,
  minimum_order_amount: IFaireMoney.build({
    amount_cents: 15000,
    currency: "CAD",
  }),
  has_new_products: true,
  ships_from_country: "BEL",
  is_highlighted_brand_for_promotional_event: false,
  is_top_shop: true,
  sort_products_by_newest: false,
  based_in_country: "BEL",
  has_insider_free_shipping: false,
  avg_brand_review_rating: "4.7",
  impression_id: "1757613525_xp5wghy8w8d5sam",
  settings_to_assign_on_view: [],
  product_images: [],
});
